/**
 * Accessibility fork for the scroll story.
 * prefers-reduced-motion → static posters + CSS fade-ins (no video scrub).
 */

export const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

export function prefersReducedMotion(
  mql: { matches: boolean } | null = null,
): boolean {
  if (mql) return mql.matches;
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  return window.matchMedia(REDUCED_MOTION_QUERY).matches;
}

export interface ReducedMotionOptions {
  /** Root element to flag. Default document.documentElement */
  root?: HTMLElement;
  /** Hide / disable video layers. */
  videos?: HTMLVideoElement[];
  /** Poster images to ensure visible. */
  posters?: HTMLElement[];
}

/**
 * Apply reduced-motion presentation:
 * - sets data-reduced-motion="true" on root for CSS fade-ins
 * - pauses and hides scrub videos
 * - ensures poster images stay visible
 */
export function applyReducedMotion(opts: ReducedMotionOptions = {}): void {
  const root =
    opts.root ??
    (typeof document !== 'undefined' ? document.documentElement : null);
  if (root) {
    root.setAttribute('data-reduced-motion', 'true');
    root.classList.add('reduced-motion');
  }

  for (const video of opts.videos ?? []) {
    try {
      video.pause();
    } catch {
      /* ignore */
    }
    video.setAttribute('aria-hidden', 'true');
    video.style.visibility = 'hidden';
    video.style.pointerEvents = 'none';
  }

  for (const poster of opts.posters ?? []) {
    poster.style.visibility = 'visible';
    poster.removeAttribute('hidden');
    poster.classList.add('rm-fade-in');
  }
}

/**
 * Clear reduced-motion presentation (e.g. preference changed).
 */
export function clearReducedMotion(root?: HTMLElement): void {
  const el =
    root ??
    (typeof document !== 'undefined' ? document.documentElement : null);
  if (!el) return;
  el.removeAttribute('data-reduced-motion');
  el.classList.remove('reduced-motion');
}

/**
 * Wire a live listener for preference changes.
 * Returns unsubscribe.
 */
export function watchReducedMotion(
  onChange: (reduced: boolean) => void,
): () => void {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return () => undefined;
  }
  const mql = window.matchMedia(REDUCED_MOTION_QUERY);
  const handler = () => onChange(mql.matches);
  if (typeof mql.addEventListener === 'function') {
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }
  // Safari < 14
  mql.addListener(handler);
  return () => mql.removeListener(handler);
}
